import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Card, 
  Button, 
  Table, 
  FormControl, 
  InputGroup, 
  Spinner,
  Pagination,
  Modal,
  Alert
} from 'react-bootstrap';
import { FaSearch, FaArrowLeft, FaBoxOpen, FaUndo } from 'react-icons/fa';

const PatientArchive = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [patientsPerPage] = useState(10);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [patientToUnarchive, setPatientToUnarchive] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArchivedPatients = async () => {
      try {
        const res = await axios.get('/api/patients.php?archived=1');
        setPatients(res.data);
        setFilteredPatients(res.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Failed to load archived patients');
        setLoading(false);
      }
    };

    fetchArchivedPatients();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = patients.filter(
        patient =>
          patient.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          patient.patientId.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (patient.contactNumber && patient.contactNumber.includes(searchQuery))
      );
      setFilteredPatients(filtered);
      setCurrentPage(1);
    } else {
      setFilteredPatients(patients);
    }
  }, [searchQuery, patients]);

  // Handle unarchive patient
  const handleUnarchiveClick = (patient) => {
    setPatientToUnarchive(patient);
    setShowUnarchiveModal(true);
  };

  const handleConfirmUnarchive = async () => {
    if (!patientToUnarchive) return;
    
    try {
      await axios.put(`/api/patients.php?id=${patientToUnarchive.id}&action=unarchive`);
      
      // Remove patient from state
      setPatients(patients.filter(p => p.id !== patientToUnarchive.id));
      setShowUnarchiveModal(false);
      setPatientToUnarchive(null);
    } catch (err) {
      console.error(err);
      setError('Failed to unarchive patient');
    }
  };

  // Pagination
  const indexOfLastPatient = currentPage * patientsPerPage;
  const indexOfFirstPatient = indexOfLastPatient - patientsPerPage;
  const currentPatients = filteredPatients.slice(indexOfFirstPatient, indexOfLastPatient);

  const paginate = pageNumber => setCurrentPage(pageNumber);

  // Get page numbers
  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredPatients.length / patientsPerPage); i++) {
    pageNumbers.push(i);
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Patient Archive</h2>
          <p className="text-muted">Archived patients are hidden from regular views</p>
        </div>
        <Link to="/patients">
          <Button variant="secondary">
            <FaArrowLeft className="me-2" /> Back to Patients
          </Button>
        </Link>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header>
          <InputGroup>
            <FormControl
              placeholder="Search by name, ID or contact number"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <Button variant="outline-secondary">
              <FaSearch />
            </Button>
          </InputGroup>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredPatients.length > 0 ? (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Patient ID</th>
                    <th>Name</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Contact</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPatients.map(patient => (
                    <tr key={patient.id}>
                      <td className="patient-id">{patient.patientId}</td>
                      <td>{patient.name}</td>
                      <td>{patient.age}</td>
                      <td>{patient.gender}</td>
                      <td>{patient.contactNumber}</td>
                      <td>
                        <Link to={`/patients/${patient.id}`} className="btn btn-sm btn-info me-2">
                          View
                        </Link>
                        <Button 
                          variant="success" 
                          size="sm" 
                          onClick={() => handleUnarchiveClick(patient)}
                          title="Unarchive Patient"
                        >
                          <FaUndo className="me-1" /> Unarchive
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Pagination className="justify-content-center">
                <Pagination.Prev
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                />
                
                {pageNumbers.map(number => (
                  <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => paginate(number)}
                  >
                    {number}
                  </Pagination.Item>
                ))}
                
                <Pagination.Next
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, pageNumbers.length))
                  }
                  disabled={currentPage === pageNumbers.length}
                />
              </Pagination>
            </>
          ) : (
            <div className="text-center my-5">
              <FaBoxOpen size={40} className="text-muted mb-3" />
              <p>No archived patients found.</p>
              <Link to="/patients">
                <Button variant="secondary">
                  <FaArrowLeft className="me-2" /> Back to Patients
                </Button>
              </Link>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Unarchive Confirmation Modal */}
      <Modal show={showUnarchiveModal} onHide={() => setShowUnarchiveModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Unarchive</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to unarchive the patient <strong>{patientToUnarchive?.name}</strong>?
          <p className="mt-2">
            This will make the patient visible in the main patient list again.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUnarchiveModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleConfirmUnarchive}>
            Unarchive Patient
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PatientArchive; 
